import dayjs from 'dayjs';
import { TripStatus, TripStatusNameConverter } from '../pages/FeedOrdersPage/enums';

/**
 * Priority order for trip statuses (high to low priority)
 * Shipped -> Scheduled -> Recommended -> Delivered
 */
export const ORDER_PRIORITY = [TripStatus.Shipped, TripStatus.Scheduled, TripStatus.Recommended, TripStatus.Delivered];

/**
 * Collects all orders from bins in a bin set
 * @param {Array} bins - Array of bin objects
 * @returns {Array} Array of all orders from all bins
 */
export function collectOrdersFromBins(bins = []) {
  return bins.reduce((arr, bin) => {
    if (bin?.last_delivery) arr.push(...bin.last_delivery);
    if (bin?.scheduled_delivery) arr.push(...bin.scheduled_delivery);
    if (bin?.recommended_delivery) arr.push(...bin.recommended_delivery);
    if (bin?.shipped_delivery) arr.push(...bin.shipped_delivery);
    return arr;
  }, []);
}

/**
 * Sorts orders by priority and time
 * @param {Array} orders - Array of order objects
 * @returns {Array} Sorted array of orders
 */
export function sortOrdersByPriorityAndTime(orders = []) {
  return orders.sort((a, b) => {
    const priorityA = ORDER_PRIORITY.indexOf(TripStatusNameConverter[a.status]);
    const priorityB = ORDER_PRIORITY.indexOf(TripStatusNameConverter[b.status]);

    // Sort by priority first
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // If same priority, sort by closest time to now
    const timeA = a.delivered_at || a.ordered_at;
    const timeB = b.delivered_at || b.ordered_at;
    const now = dayjs().unix();

    // The time math depends on the status. Generally we want the one closest to now
    // so take the diff from now to give us the closest time to now
    // eg A happened 5s and B is scheduled in 10s. 5-10 = -5 meaning A is higher priority (earlier in the list)
    return Math.abs(now - timeA) - Math.abs(now - timeB);
  });
}

/**
 * Gets the highest priority order from a bin set
 * @param {Object} binSet - Bin set object containing bins
 * @returns {Object|null} The highest priority order or null if none found
 */
export function getHighestPriorityOrder(binSet) {
  const bins = binSet?.bins || [];
  const allOrders = collectOrdersFromBins(bins);
  const sortedOrders = sortOrdersByPriorityAndTime(allOrders);
  return sortedOrders[0] || null;
}

/**
 * Calculates the last delivery time for a bin set
 * @param {Array} bins - Array of bin objects
 * @returns {number|null} Unix timestamp of the most recent delivery or null
 */
export function calculateLastDeliveryTime(bins = []) {
  return (
    bins.reduce((lastDelivery, bin) => {
      const binLastDelivery = bin?.last_delivery?.[0]?.delivered_at || 0;
      return Math.max(lastDelivery, binLastDelivery);
    }, 0) || null
  );
}

/**
 * Transforms bins data for use in status cards
 * @param {Array} bins - Array of bin objects from GraphQL
 * @returns {Array} Transformed bins array
 */
export function transformBinsForStatusCard(bins = []) {
  return bins.map((bin) => ({
    id: bin?.id || '',
    capacityInGrams: bin?.capacity_in_grams || 0,
    name: bin?.name || '?',
    lastDelivery: bin?.last_delivery?.[0]?.delivered_at || null,
  }));
}

/**
 * Complete order processing for a bin set - combines all the above functions
 * @param {Object} binSet - Bin set object from GraphQL
 * @returns {Object} Object containing processed order and bin data
 */
export function processBinSetOrders(binSet) {
  const bins = binSet?.bins || [];
  const transformedBins = transformBinsForStatusCard(bins);
  const highestPriorityOrder = getHighestPriorityOrder(binSet);
  const lastDeliveryTime = calculateLastDeliveryTime(bins);

  return {
    bins: transformedBins,
    order: highestPriorityOrder,
    lastDeliveryTime,
  };
}
